import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { fetchRewardCoupons, fetchSpinHistory, fetchSpinStatus, spin } from '../api/rewards-api.js';

const SPIN_STATUS_KEY = ['storefront', 'rewards', 'spin-status'] as const;
const SPIN_HISTORY_KEY = (limit: number, offset: number) =>
  ['storefront', 'rewards', 'spin-history', limit, offset] as const;
const REWARD_COUPONS_KEY = (onlyAvailable: boolean) =>
  ['storefront', 'rewards', 'coupons', onlyAvailable] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new StorefrontApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof StorefrontApiError && [401, 403, 404, 409, 422].includes(error.status);
}

export function useSpinStatus() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: SPIN_STATUS_KEY,
    queryFn: async ({ signal }) => fetchSpinStatus({ token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 10_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

/** Wraps `POST /rewards/spin`. On success the new `SpinStatus` implied by
 * the response (`canSpin: false`, the returned `nextEligibleAt`) is written
 * straight into the `spin-status` query cache so a second click can't even
 * be attempted before the next background refetch -- callers should also
 * keep the trigger `disabled` while `isPending`, this is defense in depth,
 * not a replacement for that. */
export function useSpinMutation() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => spin({ token: await requireToken(getToken) }),
    onSuccess: (result) => {
      queryClient.setQueryData(SPIN_STATUS_KEY, {
        canSpin: false,
        rewardConfigId: queryClient.getQueryData<{ rewardConfigId: string | null }>(SPIN_STATUS_KEY)
          ?.rewardConfigId ?? null,
        nextEligibleAt: result.nextEligibleAt,
      });
      void queryClient.invalidateQueries({ queryKey: ['storefront', 'rewards', 'spin-history'] });
      void queryClient.invalidateQueries({ queryKey: ['storefront', 'rewards', 'coupons'] });
    },
  });
}

export function useSpinHistory(params: { limit?: number; offset?: number } = {}) {
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: SPIN_HISTORY_KEY(limit, offset),
    queryFn: async ({ signal }) =>
      fetchSpinHistory({ limit, offset }, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useRewardCoupons(params: { onlyAvailable: boolean }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: REWARD_COUPONS_KEY(params.onlyAvailable),
    queryFn: async ({ signal }) =>
      fetchRewardCoupons({ onlyAvailable: params.onlyAvailable }, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}
